import React from "react"
import { Eye, Heart, MessageCircle, Share2, Bookmark, ExternalLink, Sparkles, Hash } from "lucide-react"
import { INFLUENCER_POST_CONTENT_TYPE_META, INFLUENCER_POST_SENTIMENT_META } from "../constants"
import { formatViews, formatDuration, formatRelativeTime, needsImageProxy, proxiedImageUrl } from "../utils"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

const Chip = ({ label, color }) => (
  <span style={{ background: `${color}18`, color, fontSize: "10.5px", fontWeight: 600, padding: "3px 8px", borderRadius: "6px", whiteSpace: "nowrap" }}>
    {label}
  </span>
)

/**
 * One post inside an AppCoverageCard (INFLUENCER_PROFILE_API.md §3's "post"
 * shape). No embedded player - the API gives only a permalink (`url`), no
 * playable-file field, so this stays a "watch on <platform>" link-out, same
 * as UGC Explorer's VideoCard does for its TikTok cards. `title` is
 * YouTube-only (null elsewhere, where `caption` leads instead); metrics are
 * rendered per-field since every platform reports a different subset.
 */
const InfluencerPostCard = ({ post, platform, username }) => {
  const { trackEvent } = useAnalytics()
  const heading = post.title || post.caption || "Untitled post"
  const summary = post.ai_summary
  const contentType = summary?.available ? (INFLUENCER_POST_CONTENT_TYPE_META[summary.content_type] || INFLUENCER_POST_CONTENT_TYPE_META.unknown) : null
  const sentiment = summary?.available ? (INFLUENCER_POST_SENTIMENT_META[summary.sentiment] || INFLUENCER_POST_SENTIMENT_META.unknown) : null
  // §"ai_summary" - the branch that actually matters: transcript/caption are
  // grounded reads, title_description/metadata are hedged (written from the
  // title/tags/account alone, no caption or transcript existed).
  const isHedged = summary?.based_on === "title_description" || summary?.based_on === "metadata"
  const thumbSrc = needsImageProxy(post.thumbnail_url) ? proxiedImageUrl(post.thumbnail_url) : post.thumbnail_url

  return (
    <div style={{ background: "#21222d", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "14px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <a
        href={post.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`Watch on ${post.platform}`}
        onClick={() => trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_OUTBOUND_CLICKED, {
          platform: post.platform || platform,
          username,
          link_type: "post",
          video_id: post.video_id,
          post_url: post.url,
        })}
        style={{ position: "relative", display: "block", width: "100%", paddingTop: "56.25%", background: "#000" }}
      >
        {thumbSrc ? (
          <img
            src={thumbSrc}
            alt={heading}
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.3)" }}>
            <ExternalLink size={22} />
          </div>
        )}
        {formatDuration(post.duration_seconds) && (
          <span style={{ position: "absolute", bottom: "8px", right: "8px", background: "rgba(0,0,0,0.8)", color: "#fff", fontSize: "11px", fontWeight: 600, padding: "2px 6px", borderRadius: "5px", fontFamily: "Rubik" }}>
            {formatDuration(post.duration_seconds)}
          </span>
        )}
        <span style={{ position: "absolute", top: "8px", right: "8px", width: "26px", height: "26px", borderRadius: "50%", background: "rgba(0,0,0,0.8)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ExternalLink size={12} />
        </span>
      </a>

      <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
        <p
          style={{ color: "#fff", fontSize: "13px", fontWeight: 600, margin: 0, lineHeight: "1.45", fontFamily: "Rubik", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          title={heading}
        >
          {heading}
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", fontSize: "11.5px", color: "#9ca3af" }}>
          {post.metrics?.views != null && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Eye size={11} /> {formatViews(post.metrics.views)}</span>}
          {post.metrics?.likes != null && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Heart size={11} /> {formatViews(post.metrics.likes)}</span>}
          {post.metrics?.comments != null && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><MessageCircle size={11} /> {formatViews(post.metrics.comments)}</span>}
          {post.metrics?.shares != null && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Share2 size={11} /> {formatViews(post.metrics.shares)}</span>}
          {post.metrics?.saves != null && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Bookmark size={11} /> {formatViews(post.metrics.saves)}</span>}
          {formatRelativeTime(post.published_at) && (
            <span title={post.published_at_is_estimate ? "Estimated from the platform's own relative date" : undefined}>
              {formatRelativeTime(post.published_at)}{post.published_at_is_estimate ? "*" : ""}
            </span>
          )}
        </div>

        {(contentType || sentiment) && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {contentType && <Chip label={contentType.label} color={contentType.color} />}
            {sentiment && <Chip label={sentiment.label} color={sentiment.color} />}
            {isHedged && (
              <span title="No caption or transcript was available - this summary was inferred from the title, tags and account alone." style={{ display: "flex", alignItems: "center", gap: "3px", color: "#6b7280", fontSize: "10.5px" }}>
                <Sparkles size={10} /> AI-inferred
              </span>
            )}
          </div>
        )}

        {summary?.available && summary.text && (
          <p style={{ color: "#adb5bd", fontSize: "12px", lineHeight: "1.5", margin: 0 }}>{summary.text}</p>
        )}

        {summary?.available && Array.isArray(summary.key_points) && summary.key_points.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: "16px", color: "#9ca3af", fontSize: "11.5px", lineHeight: "1.6" }}>
            {summary.key_points.slice(0, 5).map((point, i) => <li key={i}>{point}</li>)}
          </ul>
        )}

        {summary?.available && summary.angle && (
          <p style={{ color: "#6b7280", fontSize: "11.5px", margin: 0, fontStyle: "italic" }}>Angle: {summary.angle}</p>
        )}

        {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginTop: "auto" }}>
            <Hash size={11} color="#6b7280" />
            {post.hashtags.slice(0, 4).map((tag, i) => (
              <span key={i} style={{ color: "#8b9bd1", fontSize: "11px" }}>#{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default InfluencerPostCard
